import dayjs from 'dayjs';

import {
    AccountFlexControlsFormInputs,
    AccountFlexControlsPayload,
    CustomerFlexControlsFormInputs,
    CustomerFlexControlsPayload,
} from '@clients/domains/flexControls/flexControlsClient';
import { ResetPeriodCode } from '@utils/resetPeriod';
import { stringToArray } from '@utils/string/stringToArray';

export const formatAccountFlexControlInputs = (
    data: AccountFlexControlsFormInputs,
) => {
    const convertedValues = {
        name: data.name,
        active: data.active,
        beneficiary_id: data.beneficiary_id,
        currency_code: data.currency_code,
        deny_code: data.deny_code,
        limit_duration: data.limit_duration,
        time: data.time,
        time_zone: data.time_zone,
        type: data.type,
        week_day: data.week_day,
        month_day: data.month_day,
        conditions: data.conditions,
    } as AccountFlexControlsPayload;

    return convertValues(data, convertedValues);
};

export const formatCustomerFlexControlInputs = (
    data: CustomerFlexControlsFormInputs,
) => {
    const convertedValues = {
        name: data.name,
        active: data.active,
        beneficiary_id: data.beneficiary_id,
        currency_code: data.currency_code,
        deny_code: data.deny_code,
        limit_duration: data.limit_duration,
        time: data.time,
        time_zone: data.time_zone,
        type: data.type,
        week_day: data.week_day,
        month_day: data.month_day,
        conditions: data.conditions,
        account_id: data.account_id,
        card_ids: data.card_ids,
    } as CustomerFlexControlsPayload;

    return convertValues(data, convertedValues);
};

const convertValues = (
    data: CustomerFlexControlsFormInputs | AccountFlexControlsFormInputs,
    convertedValues: CustomerFlexControlsPayload | AccountFlexControlsPayload,
) => {
    if (data.time !== undefined) {
        // Pass dayjs a random date as passing it only a time returns an invalid date
        convertedValues.time = dayjs(`11-11-11 ${data.time}`)
            .format('LT')
            // remove space between time and AM/PM
            .replace(' ', '');
    }

    // Use correct format for API

    if (data.limit_duration !== undefined) {
        convertedValues.limit_duration = ResetPeriodCode(
            convertedValues.limit_duration,
        );
    }

    // Delete key if attribute empty to avoid bad data being sent
    if (Array.isArray(data.conditions)) {
        if (data.conditions[0]?.attribute === undefined) {
            delete convertedValues.conditions;
        }
    }

    if (data.conditions?.length === 0) {
        convertedValues.conditions = [];
    }

    if (data.max_limit !== undefined) {
        convertedValues.max_limit = Number(data.max_limit);
    }

    if (data.override_controls !== undefined) {
        if (data.override_controls?.length > 0) {
            convertedValues.override_controls = stringToArray(
                data.override_controls as string,
                ',',
            );
        } else {
            convertedValues.override_controls = [];
        }
    } else {
        delete convertedValues.override_controls;
    }

    if (data.processing_codes !== undefined) {
        if (data.processing_codes?.length > 0) {
            convertedValues.processing_codes = stringToArray(
                data.processing_codes as string,
                ',',
            );
        } else {
            convertedValues.processing_codes = [];
        }
    } else {
        delete convertedValues.processing_codes;
    }
    // it's safe to do a string check here as it's invalid to remove a timezone using an empty string
    if (data.time_zone === '') {
        delete convertedValues.time_zone;
    }

    if (data.limit_duration === 'monthly') {
        convertedValues.reset_period = {
            month_day: Number(data.month_day),
            // use a dummy date to allow dayjs to format properly
            time: dayjs(`01-01-2001 ${data.time}`).format('hh:mmA'),
        };
    } else if (data.limit_duration === 'weekly') {
        convertedValues.reset_period = {
            week_day: data.week_day,
            time: dayjs(`01-01-2001 ${data.time}`).format('hh:mmA'),
        };
    } else {
        convertedValues.reset_period = {
            time: dayjs(`01-01-2001 ${data.time}`).format('hh:mmA'),
        };
    }

    delete convertedValues.week_day;
    delete convertedValues.month_day;
    delete convertedValues.time;

    return convertedValues;
};
